import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Edit2,
  CheckSquare,
  Star,
  X,
  GripVertical,
  AlertTriangle,
  UserCheck,
} from "lucide-react";
import {
  approvalTemplatesApi,
  approvalRolesApi,
  type ApprovalTemplate,
  type ChecklistItem,
} from "../../api/client";

interface TemplateFormData {
  name: string;
  role_id: string;
  is_default: boolean;
  checklist: ChecklistItem[];
}

export default function ApprovalTemplatesSettings() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ApprovalTemplate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: "",
    role_id: "",
    is_default: false,
    checklist: [],
  });
  const [newItemText, setNewItemText] = useState("");
  const [newItemRequired, setNewItemRequired] = useState(false);

  // Fetch templates
  const {
    data: templatesData,
    isLoading: templatesLoading,
    error: templatesError,
  } = useQuery({
    queryKey: ["approval-templates"],
    queryFn: () => approvalTemplatesApi.list(),
  });

  // Fetch roles dynamically
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["approval-roles", { active_only: true }],
    queryFn: () => approvalRolesApi.list(true),
  });

  const createMutation = useMutation({
    mutationFn: approvalTemplatesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-templates"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof approvalTemplatesApi.update>[1];
    }) => approvalTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-templates"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: approvalTemplatesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-templates"] });
      setDeleteConfirm(null);
    },
  });

  const templates = templatesData?.templates || [];
  const roles = rolesData?.roles || [];

  // Group templates by role
  const templatesByRole = useMemo(() => {
    if (!roles.length) return [];

    return roles.map((role) => ({
      role,
      templates: templates.filter(
        (t) =>
          t.role_id === role.id ||
          t.role_slug === role.slug ||
          t.role === role.slug,
      ),
    }));
  }, [roles, templates]);

  // Templates with legacy roles that don't match any current role
  const legacyTemplates = useMemo(() => {
    const knownRoleSlugs = new Set(roles.map((r) => r.slug));
    const knownRoleIds = new Set(roles.map((r) => r.id));
    return templates.filter(
      (t) =>
        !t.role_id &&
        t.role &&
        !knownRoleSlugs.has(t.role) &&
        !knownRoleIds.has(t.role_id || ""),
    );
  }, [roles, templates]);

  const resetForm = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({
      name: "",
      role_id: roles.length > 0 ? roles[0].id : "",
      is_default: false,
      checklist: [],
    });
    setNewItemText("");
    setNewItemRequired(false);
  };

  const handleEdit = (template: ApprovalTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      role_id: template.role_id || "",
      is_default: template.is_default,
      checklist: template.checklist || [],
    });
    setShowForm(true);
  };

  const handleAddChecklistItem = () => {
    if (!newItemText.trim()) return;

    const newItem: ChecklistItem = {
      id: `item-${Date.now()}`,
      text: newItemText.trim(),
      required: newItemRequired,
    };

    setFormData((prev) => ({
      ...prev,
      checklist: [...prev.checklist, newItem],
    }));
    setNewItemText("");
    setNewItemRequired(false);
  };

  const handleRemoveChecklistItem = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      checklist: prev.checklist.filter((item) => item.id !== itemId),
    }));
  };

  const handleToggleRequired = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      checklist: prev.checklist.map((item) =>
        item.id === itemId ? { ...item, required: !item.required } : item,
      ),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name: formData.name,
      role_id: formData.role_id,
      is_default: formData.is_default,
      checklist: formData.checklist,
    };

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleOpenForm = () => {
    setFormData({
      name: "",
      role_id: roles.length > 0 ? roles[0].id : "",
      is_default: false,
      checklist: [],
    });
    setShowForm(true);
  };

  if (templatesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto" />
          <p className="mt-2 text-gray-600">
            Failed to load approval templates
          </p>
        </div>
      </div>
    );
  }

  const isLoading = templatesLoading || rolesLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Approval Templates
          </h1>
          <p className="mt-1 text-gray-600">
            Create reusable checklists for different approver roles
          </p>
        </div>
        <button
          onClick={handleOpenForm}
          disabled={roles.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          New Template
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : roles.length === 0 ? (
        // No roles defined - show message to create roles first
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <UserCheck className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No Approval Roles Defined
          </h3>
          <p className="mt-2 text-gray-600 max-w-md mx-auto">
            Before creating templates, you need to define approval roles. Roles
            determine who can approve deployment requests.
          </p>
          <a
            href="/settings/approval-roles"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Create Approval Roles
          </a>
        </div>
      ) : templates.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <CheckSquare className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No templates yet
          </h3>
          <p className="mt-2 text-gray-600">
            Create approval templates with checklists for different reviewer
            roles.
          </p>
          <button
            onClick={handleOpenForm}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Create First Template
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {templatesByRole.map(({ role, templates: roleTemplates }) => (
            <div key={role.id}>
              <div className="flex items-center gap-2 mb-3">
                <UserCheck className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {role.name}
                </h2>
                <span className="text-sm text-gray-500">
                  ({roleTemplates.length})
                </span>
              </div>

              {roleTemplates.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                  No templates for {role.name} role
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {roleTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">
                              {template.name}
                            </h3>
                            {template.is_default && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                <Star className="h-3 w-3" />
                                Default
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(template)}
                              className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                              title="Edit template"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(template.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Delete template"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {template.checklist && template.checklist.length > 0 ? (
                          <ul className="mt-3 space-y-1.5">
                            {template.checklist.map((item) => (
                              <li
                                key={item.id}
                                className="flex items-start gap-2 text-sm text-gray-600"
                              >
                                <CheckSquare className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" />
                                <span>
                                  {item.text}
                                  {item.required && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-3 text-sm text-gray-500 italic">
                            No checklist items
                          </p>
                        )}
                      </div>
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
                        {template.checklist?.length || 0} items
                        {template.checklist?.filter((i) => i.required).length
                          ? ` (${template.checklist.filter((i) => i.required).length} required)`
                          : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Legacy templates section */}
          {legacyTemplates.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Legacy Templates
                </h2>
                <span className="text-sm text-gray-500">
                  ({legacyTemplates.length})
                </span>
              </div>
              <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg mb-4">
                These templates use roles that no longer exist. Edit them to
                assign a current role.
              </p>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {legacyTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-white rounded-lg shadow border border-yellow-300 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {template.name}
                          </h3>
                          <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded mt-1 inline-block">
                            Legacy role: {template.role}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(template)}
                            className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                            title="Edit template"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(template.id)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                            title="Delete template"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTemplate ? "Edit Template" : "New Template"}
              </h2>
              <button
                onClick={resetForm}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto p-6"
            >
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Template Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="e.g., Production Security Review"
                    required
                  />
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approver Role
                  </label>
                  {roles.length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        No approval roles defined yet.{" "}
                        <a
                          href="/settings/approval-roles"
                          className="text-yellow-900 underline font-medium"
                        >
                          Create approval roles
                        </a>{" "}
                        first to create templates.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {roles.map((role) => (
                        <label
                          key={role.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                            formData.role_id === role.id
                              ? "border-indigo-500 bg-indigo-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="role_id"
                            value={role.id}
                            checked={formData.role_id === role.id}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                role_id: e.target.value,
                              }))
                            }
                            className="sr-only"
                          />
                          <UserCheck className="h-5 w-5 text-indigo-600" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block">
                              {role.name}
                            </span>
                            {role.description && (
                              <span className="text-xs text-gray-500 truncate block">
                                {role.description}
                              </span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Default Toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_default: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="is_default" className="text-sm text-gray-700">
                    Set as default template for this role
                  </label>
                </div>

                {/* Checklist Builder */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Checklist Items
                  </label>

                  {formData.checklist.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {formData.checklist.map((item, index) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group"
                        >
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500 w-6">
                            {index + 1}.
                          </span>
                          <span className="flex-1 text-sm">{item.text}</span>
                          <button
                            type="button"
                            onClick={() => handleToggleRequired(item.id)}
                            className={`px-2 py-0.5 text-xs rounded ${
                              item.required
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {item.required ? "Required" : "Optional"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveChecklistItem(item.id)}
                            className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newItemText}
                      onChange={(e) => setNewItemText(e.target.value)}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      placeholder="Add checklist item..."
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddChecklistItem();
                        }
                      }}
                    />
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newItemRequired}
                        onChange={(e) => setNewItemRequired(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={handleAddChecklistItem}
                      disabled={!newItemText.trim()}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  !formData.name ||
                  !formData.role_id ||
                  createMutation.isPending ||
                  updateMutation.isPending
                }
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : editingTemplate
                    ? "Update Template"
                    : "Create Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Delete Template
            </h3>
            <p className="mt-2 text-gray-600">
              Are you sure you want to delete this template? This action cannot
              be undone.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
